import { memo, useCallback, useMemo, useRef } from 'react'

import { Chip, Stack, useDialogState } from '@collaborate-health/uikit'
import { useDispatch, useSelector } from '@tabeeb/store'
import {
  ControlElement,
  Form,
  FormApi,
  FormElement,
  FormProps,
  IAnswer,
  IForm,
  IOptionAnswer,
  IOptionsAnswer,
  IValueAnswer,
} from '@collaborate-health/forms'
import {
  getCurrentForm,
  getCurrentFormAnswers,
  getCurrentFormControls,
  getFormUsersFilterSelectedValue,
  getIsCurrentFormViewDisabled,
  getIsDetachButtonDisabled,
  getIsUploadAnswersFromFileButtonVisible,
  getSelectedGalleryItemId,
} from '@tabeeb/modules/forms/selectors'
import {
  ControlOptionsResponse,
  ControlType,
  CreateFormAnswerRequest,
  FormAnswerStatus,
  FormControlAnswerResponse,
} from '@tabeeb/contracts/main-api/swagger-main-api'

import {
  mapFormActionControlToElement,
  mapFormControlToControlElement,
  mapFormCounterToElement,
  mapFormTabToElement,
} from '@tabeeb/modules/createFormPage/services/mapping'

import { Divider } from '@mui/material'
import { UploadOutlined } from '@mui/icons-material'
import { useFormTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'
import moment from 'moment'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { getCurrentVideoTimestamp } from '@tabeeb/modules/player/selectors'
import { updatePageNameRequest } from '@tabeeb/modules/gallery/actions'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import SignatureViewer from '../Signature/SignatureViewer'
import FormQuestion from '../FormQuestion'

import { ContentFormsElementsRegistryContextProvider } from '../../contexts'
import { FormControl } from '../../types'

import FormsButtons from '../FormsButtons'
import SubmitFormButton from '../SubmitFormButton'
import UsersFilter from '../UsersFilter'
import FormRecords from '../FormRecords'
import InitialValuesButton from '../InitialValuesButton'
import AnswerStatusStatistics from '../AnswerStatusStatistics'
import UploadFormAnswersDialog from '../UploadFormAnswersDialog'
import { setIsAnswerInProgress } from '../../actions'

export interface CurrentFormProps {
  readonly?: boolean
}

function optionsAreEqual(prevArray?: number[], nextArray?: number[]): boolean {
  const prev = prevArray ?? []
  const next = nextArray ?? []

  return prev.length === next.length && prev.every((value) => next.includes(value))
}

const CurrentForm: React.FC<CurrentFormProps> = ({ readonly = false }) => {
  const dispatch = useDispatch()

  const { mainApi: api } = useApi()
  const formApiRef = useRef<FormApi>(undefined!)

  const { f } = useFormTitleFormatter()

  const usersFilterValue = useSelector(getFormUsersFilterSelectedValue)
  const currentPageId = useSelector(getSelectedGalleryItemId)
  const contentId = useSelector(getContentId)
  const contentForm = useSelector(getCurrentForm)

  const contentFormRef = useRef(contentForm)
  contentFormRef.current = contentForm

  const contentFormAnswers = useSelector(getCurrentFormAnswers)
  const disabled = useSelector(getIsCurrentFormViewDisabled)
  const timestamp = useSelector(getCurrentVideoTimestamp)

  const timestampRef = useRef(timestamp)
  timestampRef.current = timestamp

  const [uploadAnswersDialogOpen, onOpenUploadAnswersDialog, onCloseUploadAnswersDialog] = useDialogState()

  const isUploadAnswersButtonVisible = useSelector(getIsUploadAnswersFromFileButtonVisible)
  const isUploadAnswersButtonDisabled = useSelector((state) => getIsDetachButtonDisabled(state, {}))

  const filteredByAnswerStatusControls = useSelector(getCurrentFormControls)

  const filteredControlsIdsSet = useMemo(() => {
    return new Set(filteredByAnswerStatusControls.map((c) => c.Id))
  }, [filteredByAnswerStatusControls])

  const form = useMemo((): IForm => {
    const elements: FormElement[] = []

    for (const tab of contentForm?.Tabspanels || []) {
      const tabElement = mapFormTabToElement(tab)
      tabElement.order -= 1000
      tabElement.sticky = true

      elements.push(tabElement)
    }

    for (const control of contentForm?.Controls || []) {
      if (control.IsHidden || !filteredControlsIdsSet.has(control.Id)) {
        continue
      }

      elements.push(mapFormControlToControlElement(control, contentForm?.Controls ?? []))
    }

    for (const action of contentForm?.Actions || []) {
      elements.push(mapFormActionControlToElement(action))
    }

    for (const counter of contentForm?.Counters || []) {
      elements.push(mapFormCounterToElement(counter))
    }

    return {
      name: '',
      elements,
    }
  }, [
    contentForm?.Actions,
    contentForm?.Controls,
    contentForm?.Counters,
    contentForm?.Tabspanels,
    filteredControlsIdsSet,
  ])

  const answers = useMemo((): IAnswer[] => {
    return (contentFormAnswers as (FormControlAnswerResponse & { controlType: ControlType })[])
      .filter((a) => a.FormAnswerStatus !== FormAnswerStatus.Empty)
      .map((answer) => {
        if (answer.controlType === ControlType.Selectlist || answer.controlType === ControlType.Radiobuttonlist) {
          const optionAnswer: IOptionAnswer = {
            type: 'option',
            controlId: answer.FormControlId.toString(),
            value: answer.SelectedOptions!.map((option) => ({
              id: option.Id.toString(),
              value: option.Value!,
              order: 0,
            }))[0],
            meta: answer,
          }

          if (!optionAnswer.value) {
            return null
          }

          return optionAnswer
        }

        if (answer.controlType === ControlType.Checkboxlist) {
          const optionsAnswer: IOptionsAnswer = {
            type: 'options',
            controlId: answer.FormControlId.toString(),
            value: answer.SelectedOptions!.map((option, index) => ({
              id: option.Id.toString(),
              value: option.Value!,
              order: index,
            })),
            meta: answer,
          }

          return optionsAnswer
        }

        const valueAnswer: IValueAnswer<string> = {
          type: 'value',
          controlId: answer.FormControlId.toString(),
          value: answer.Value!,
          meta: answer,
        }

        return valueAnswer
      })
      .filter((a) => a !== null)
  }, [contentFormAnswers])

  const onSubmitFormAnswer = useCallback(
    async (answer: IAnswer<unknown, ControlOptionsResponse>, controlElement: ControlElement<FormControl>) => {
      const control = controlElement.meta!
      const currentContentForm = contentFormRef.current

      const newAnswerRequest: CreateFormAnswerRequest = {
        ContentId: contentId,
        ContentFormId: currentContentForm.ContentFormId!,
        FormControlId: control.Id,
        PageId: currentPageId,
        Timestamp: timestampRef.current ?? -1,
        TimeZone: moment.tz.guess(),
        Value: `${answer.value}`.trim(),
        OptionsIds: [],
        FormTabsIds: currentContentForm.selectedTabs,
      }

      if (answer.type === 'option') {
        newAnswerRequest.Value = answer.value.value
        newAnswerRequest.OptionsIds = [answer.value.meta!.Id!]
      }

      if (answer.type === 'options') {
        newAnswerRequest.Value = answer.value.map((v) => v.value).join(', ')
        newAnswerRequest.OptionsIds = answer.value.map((v) => v.meta!.Id)
      }

      const prevAnswer = contentFormAnswers.find((a) => a.FormControlId === control.Id)

      if (
        (newAnswerRequest.Value === prevAnswer?.Value || (!prevAnswer && !newAnswerRequest.Value)) &&
        optionsAreEqual(prevAnswer?.selectedOptionsIds, newAnswerRequest.OptionsIds ?? undefined)
      ) {
        return false
      }

      await api.formAnswers.postAsync(newAnswerRequest)

      return true
    },
    [contentFormAnswers, contentId, currentPageId, api.formAnswers]
  )

  const onSubmitSingle = useCallback(
    async (answer: IAnswer<unknown, ControlOptionsResponse>, control: ControlElement<FormControl>) => {
      try {
        dispatch(setIsAnswerInProgress(true))

        const submitted = await onSubmitFormAnswer(answer, control)
        if (!submitted) {
          return
        }

        dispatch(updatePageNameRequest({ PageId: currentPageId, ContentId: contentId }))
      } catch {
        // ignored
      } finally {
        dispatch(setIsAnswerInProgress(false))
      }
    },
    [onSubmitFormAnswer, dispatch, currentPageId, contentId]
  )

  const onSubmitAll = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        dispatch(setIsAnswerInProgress(true))

        const answersToSubmit: { answer: IAnswer; control: ControlElement<FormControl> }[] = []
        for (const [controlId, value] of Object.entries(data)) {
          if (value === undefined) {
            continue
          }

          const control = form.elements.find((el) => el.id === controlId) as ControlElement<FormControl>
          if (!control) {
            continue
          }

          let answer: IAnswer = null!

          if (control.type === 'selectlist' || control.type === 'radiobuttonlist') {
            const selectedOption = control.options.values.find((option) => option.id === value)
            if (!selectedOption) {
              continue
            }

            answer = {
              type: 'option',
              controlId,
              value: selectedOption,
            }
          }

          if (control.type === 'checkboxlist') {
            const selectedOptions = control.options.values.filter((option) =>
              (value as string[] | undefined)?.includes(option.id)
            )
            if (selectedOptions.length === 0) {
              continue
            }

            answer = {
              type: 'options',
              controlId,
              value: selectedOptions,
            }
          }

          answer ??= {
            type: 'value',
            controlId,
            value: value as string,
          }

          answersToSubmit.push({ answer, control })
        }

        await Promise.all(
          answersToSubmit.map(({ answer, control }) =>
            onSubmitFormAnswer(answer as IAnswer<unknown, ControlOptionsResponse>, control)
          )
        )

        dispatch(updatePageNameRequest({ PageId: currentPageId, ContentId: contentId }))
        dispatch(onAddSuccessNotification({ message: f('Form answers submitted successfully') }))
      } catch {
        dispatch(onAddErrorNotification({ message: f('Failed to submit form answers') }))
      } finally {
        dispatch(setIsAnswerInProgress(false))
      }
    },
    [contentId, currentPageId, dispatch, f, form.elements, onSubmitFormAnswer]
  )

  const submitMode = useMemo((): FormProps['submitMode'] => {
    if (contentForm?.Controls?.some((c) => c.IsRequired || c.Validations?.length || c.Dependencies?.length)) {
      return 'all'
    }

    return 'perAnswer'
  }, [contentForm?.Controls])

  return (
    <ContentFormsElementsRegistryContextProvider>
      {!readonly && (
        <Stack spacing={1} paddingInline={1.5}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <UsersFilter disabled={readonly} />
            <FormRecords />
          </Stack>

          <InitialValuesButton />

          {isUploadAnswersButtonVisible && (
            <>
              <Chip
                icon={<UploadOutlined />}
                disabled={isUploadAnswersButtonDisabled}
                color='primary'
                onClick={onOpenUploadAnswersDialog}
                label={f(`Upload form answers`)}
              />

              <UploadFormAnswersDialog
                open={uploadAnswersDialogOpen}
                formRef={formApiRef}
                onClose={onCloseUploadAnswersDialog}
              />
            </>
          )}

          <AnswerStatusStatistics />
        </Stack>
      )}

      <Stack flex={1} height='100%' overflow='hidden'>
        <Divider />
        <Stack flexGrow={1} overflow='auto' position='relative' spacing={2}>
          <Form
            apiRef={formApiRef}
            key={`${usersFilterValue}-${contentForm?.Id}`}
            form={form}
            disabled={disabled}
            readOnly={readonly}
            answers={answers}
            wrapper={FormQuestion}
            submitMode={submitMode}
            onSubmit={onSubmitAll}
            onSubmitAnswer={onSubmitSingle as FormProps['onSubmitAnswer']}
          >
            <Stack flex={1}>
              <SignatureViewer />
            </Stack>
            {submitMode === 'all' && <SubmitFormButton />}
          </Form>
        </Stack>
        <Divider />
      </Stack>
      <FormsButtons />
    </ContentFormsElementsRegistryContextProvider>
  )
}

export default memo(CurrentForm)

